import { LocaleDict, supportedLocales } from "@/i18n";
import { ComponentProps } from "react";
import { ComputeFieldComponent } from "../computeFields";
import { Multipleprint } from "../types";
import {
  DimensionsFormatting,
  hasDisplayableDimensions,
} from "./shared/dimensions";
import { InscriptionDosCF } from "./shared/inscription-dos";
import { RelatedAuthorsListCF } from "./shared/related-authors";
import { SignatureCF } from "./shared/signature";
import { SupportTechniqueCF } from "./shared/technique";
import { TitleCF } from "./shared/title";
import { SeparatedList } from "./shared/utils";

export const MultipleprintDimensionsCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionDimensionsOeuvre"
    | "estampesMultiplesDescriptionDimensionsOeuvreInconnues"
    | "estampesMultiplesDescriptionDimensionsOeuvreRaw"
  >
> = ({ input, ...props }) => (
  <DimensionsFormatting
    input={{
      ...input.estampesMultiplesDescriptionDimensionsOeuvre,
      rawDimensions: input.estampesMultiplesDescriptionDimensionsOeuvreRaw,
      unknownDimensions:
        input.estampesMultiplesDescriptionDimensionsOeuvreInconnues,
    }}
    {...props}
  />
);

export const MultipleprintDimensionImageCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionDimensionsImage"
    | "estampesMultiplesDescriptionDimensionsImageInconnues"
  >
> = ({ input, ...props }) => (
  <DimensionsFormatting
    input={{
      ...input.estampesMultiplesDescriptionDimensionsImage,
      unknownDimensions:
        input.estampesMultiplesDescriptionDimensionsImageInconnues,
    }}
    {...props}
  />
);

const MULTIPLEPRINT_SUMMARY_DIMENSIONS_LABEL_KEYS = {
  sheet: "computeField.multipleprintSummaryDimensionsSheet",
  image: "computeField.multipleprintSummaryDimensionsImage",
} as const;

export const MultipleprintSummaryDimensionsCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionDimensionsOeuvre"
    | "estampesMultiplesDescriptionDimensionsOeuvreInconnues"
    | "estampesMultiplesDescriptionDimensionsOeuvreRaw"
    | "estampesMultiplesDescriptionDimensionsImage"
    | "estampesMultiplesDescriptionDimensionsImageInconnues"
  >
> = ({ input, translation, lineBreak = false, ...props }) => {
  const customDimensions =
    input.estampesMultiplesDescriptionDimensionsOeuvreRaw?.trim();
  if (customDimensions) {
    return (
      <>
        {customDimensions}
        {lineBreak && <br />}
      </>
    );
  }

  const sheetInput = {
    ...input.estampesMultiplesDescriptionDimensionsOeuvre,
    unknownDimensions: input.estampesMultiplesDescriptionDimensionsOeuvreInconnues,
  };
  const imageInput = {
    ...input.estampesMultiplesDescriptionDimensionsImage,
    unknownDimensions:
      input.estampesMultiplesDescriptionDimensionsImageInconnues,
  };

  const hasSheet = hasDisplayableDimensions(sheetInput);
  const hasImage = hasDisplayableDimensions(imageInput);

  if (!hasSheet && !hasImage) return null;

  const renderDimensionsOnly = (
    dimInput: typeof sheetInput,
    withLineBreak: boolean,
  ) => (
    <>
      <DimensionsFormatting
        input={dimInput}
        lineBreak={false}
        {...props}
      />
      {withLineBreak && <br />}
    </>
  );

  const renderLine = (
    labelKey: (typeof MULTIPLEPRINT_SUMMARY_DIMENSIONS_LABEL_KEYS)[keyof typeof MULTIPLEPRINT_SUMMARY_DIMENSIONS_LABEL_KEYS],
    dimInput: typeof sheetInput,
    withLineBreak: boolean,
  ) => {
    const label = translation?.(labelKey);
    const formattedDimensions = (
      <DimensionsFormatting
        input={dimInput}
        lineBreak={false}
        {...props}
      />
    );

    if (!label) {
      return (
        <>
          {formattedDimensions}
          {withLineBreak && <br />}
        </>
      );
    }

    return (
      <>
        {label}{" "}
        {formattedDimensions}
        {withLineBreak && <br />}
      </>
    );
  };

  if (hasSheet && hasImage) {
    return (
      <>
        {renderLine(
          MULTIPLEPRINT_SUMMARY_DIMENSIONS_LABEL_KEYS.sheet,
          sheetInput,
          true,
        )}
        {renderLine(
          MULTIPLEPRINT_SUMMARY_DIMENSIONS_LABEL_KEYS.image,
          imageInput,
          lineBreak,
        )}
      </>
    );
  }

  if (hasSheet) {
    return renderDimensionsOnly(sheetInput, lineBreak);
  }

  return renderLine(
    MULTIPLEPRINT_SUMMARY_DIMENSIONS_LABEL_KEYS.image,
    imageInput,
    lineBreak,
  );
};

export const MultipleprintRelatedAuthorsCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "relatedAuthor"
    | "estampesMultiplesDescriptionAuteurArtisteAnonyme"
    | "estampesMultiplesDescriptionAuteurArtisteDApres"
    | "estampesMultiplesDescriptionAuteurArtisteAttribueA"
    | "estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe"
    | "estampesMultiplesDescriptionAuteurArtisteEntourageDe"
    | "estampesMultiplesDescriptionAuteurAtelierDe"
  >
> = ({ input, ...props }) => (
  <RelatedAuthorsListCF
    input={{
      relatedAuthors: input.relatedAuthor,
      attributedTo: input.estampesMultiplesDescriptionAuteurArtisteAttribueA,
      from: input.estampesMultiplesDescriptionAuteurArtisteDApres,
      tasteOf: input.estampesMultiplesDescriptionAuteurArtisteDansLeGoutDe,
      entourageOf: input.estampesMultiplesDescriptionAuteurArtisteEntourageDe,
      studioOf: input.estampesMultiplesDescriptionAuteurAtelierDe,
      anonymous: input.estampesMultiplesDescriptionAuteurArtisteAnonyme,
    }}
    {...props}
  />
);

export const MultipleprintSupportTechniqueCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionTechnique"
    | "estampesMultiplesDescriptionSupportMateriau"
    | "estampesMultiplesDescriptionOeuvreDoubleFace"
  >
> = ({ input, ...props }) => (
  <SupportTechniqueCF
    input={{
      technique: input.estampesMultiplesDescriptionTechnique,
      support: input.estampesMultiplesDescriptionSupportMateriau,
      isDoubleSided: input.estampesMultiplesDescriptionOeuvreDoubleFace,
    }}
    {...props}
  />
);

export const MultipleprintSignatureCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionSignatureLocalisation"
    | "estampesMultiplesDescriptionSignatureTranscription"
  >
> = ({ input, ...props }) => (
  <SignatureCF
    input={{
      location: input.estampesMultiplesDescriptionSignatureLocalisation,
      transcription: input.estampesMultiplesDescriptionSignatureTranscription,
    }}
    {...props}
  />
);

export const MultipleprintInscriptionDosCF: ComputeFieldComponent<
  Pick<Multipleprint, "estampesMultiplesDescriptionInscriptionsDos">
> = ({ input, ...props }) => (
  <InscriptionDosCF
    input={{
      inscription: input.estampesMultiplesDescriptionInscriptionsDos,
      published: supportedLocales.reduce((acc, locale) => {
        acc[locale] = true;
        return acc;
      }, {} as LocaleDict<boolean>),
    }}
    {...props}
  />
);

export const MultipleprintTitleCF: ComputeFieldComponent<
  Pick<
    Multipleprint,
    | "estampesMultiplesDescriptionTitreSansTitre"
    | "estampesMultiplesDescriptionTitreInconnu"
    | "estampesMultiplesDescriptionTitreIncertin"
    | "estampesMultiplesDescriptionTitre"
    | "estampesMultiplesDescriptionSousTitre"
    | "estampesMultiplesDescriptionDateDeCreation"
    | "estampesMultiplesDescriptionDateDeCreationInconnue"
    | "estampesMultiplesDescriptionDateDeCreationIncertaine"
  >
> = ({ input, ...props }) => (
  <TitleCF
    input={{
      title: input.estampesMultiplesDescriptionTitre,
      titleState: {
        none: input.estampesMultiplesDescriptionTitreSansTitre,
        unknown: input.estampesMultiplesDescriptionTitreInconnu,
        notConfirmed: input.estampesMultiplesDescriptionTitreIncertin,
      },
      subtitle: input.estampesMultiplesDescriptionSousTitre,
      date: input.estampesMultiplesDescriptionDateDeCreation,
      unknownDate: input.estampesMultiplesDescriptionDateDeCreationInconnue,
      uncertainDate: input.estampesMultiplesDescriptionDateDeCreationIncertaine,
    }}
    {...props}
  />
);

export const MultipleprintSummaryCF: ComputeFieldComponent<
  ComponentProps<typeof MultipleprintRelatedAuthorsCF>["input"] &
    ComponentProps<typeof MultipleprintTitleCF>["input"] &
    ComponentProps<typeof MultipleprintSummaryDimensionsCF>["input"] &
    ComponentProps<typeof MultipleprintSupportTechniqueCF>["input"] &
    ComponentProps<typeof MultipleprintSignatureCF>["input"] &
    ComponentProps<typeof MultipleprintInscriptionDosCF>["input"]
> = ({ input, lineBreak = true, ...props }) => {
  return (
    <SeparatedList
      lineBreak={lineBreak}
      {...props}
      input={[
        <MultipleprintRelatedAuthorsCF input={input} {...props} />,
        <MultipleprintTitleCF input={input} {...props} />,
        <MultipleprintSummaryDimensionsCF input={input} {...props} />,
        <MultipleprintSupportTechniqueCF input={input} {...props} />,
        <MultipleprintSignatureCF input={input} {...props} />,
        <MultipleprintInscriptionDosCF input={input} {...props} />,
      ]}
    />
  );
};
